import type { CodeItemGroup, CodeItemSubgroup, CodeRow, ConditionGroup, IfNode, RuleNode } from '../types/codeIndex'
import { normalizeCodeItemReference } from './codeReference'

export type PositionedSubgroup = CodeItemSubgroup & {
  start: number
  end: number
}

export type CompositeOptionRange = {
  name: string
  start: number
  end: number
  options: string[]
}

export type CompositeStructureResult = {
  group: CodeItemGroup
  patternMap: Map<string, string>
  movedOptionCount: number
}

export type ResizeIssue = {
  rowId?: string
  optionCode?: string
  reason: string
}

export type CodeItemResizeImpact = {
  affectedOptions: ResizeIssue[]
  safelyRemappedOptions: number
}

export type CodeItemResizeResult = {
  group: CodeItemGroup
  patternMap: Map<string, string>
  impact: CodeItemResizeImpact
}

export function getCodeItemDigits(group: Pick<CodeItemGroup, 'codeItemPos'>): number {
  const [start, end] = group.codeItemPos.posRange
  return Math.max(1, Number(end) - Number(start) + 1)
}

export function isPositionedCodeRow(row: Pick<CodeRow, 'valuePosition'>): boolean {
  return Number.isInteger(row.valuePosition)
}

export function getCodeRowPositionEnd(row: Pick<CodeRow, 'valuePosition' | 'valuePositionEnd'>): number | null {
  if (!Number.isInteger(row.valuePosition)) return null
  return Number.isInteger(row.valuePositionEnd) ? Number(row.valuePositionEnd) : Number(row.valuePosition)
}

function normalizeSubgroupName(value: string | null | undefined): string {
  return String(value || '').trim()
}

function makeSubgroupLocalKey(name: string, start: number, end: number, index: number): string {
  return `subgroup:${name.toLowerCase()}:${start}:${end}:${index}`
}

export function positionSubgroups(subgroups: CodeItemSubgroup[], totalDigits?: number): PositionedSubgroup[] {
  let nextStart = 0
  return subgroups.map((subgroup) => {
    const length = Math.max(1, Math.floor(Number(subgroup.length)) || 1)
    const isAnyPosition = subgroup.positionMode === 'any'
    const anyLength = Math.max(1, Math.floor(Number(totalDigits)) || length)
    const start = isAnyPosition ? 0 : nextStart
    const end = isAnyPosition ? anyLength - 1 : start + length - 1
    const positioned = {
      ...subgroup,
      positionMode: isAnyPosition ? 'any' as const : 'fixed' as const,
      length: end - start + 1,
      start,
      end,
    }
    if (!isAnyPosition) nextStart = end + 1
    return positioned
  })
}

export function deriveCompositeSubgroups(rows: CodeRow[], totalDigits?: number): CodeItemSubgroup[] {
  const ranges = new Map<string, { name: string; start: number; end: number; order: number }>()

  rows.forEach((row, rowIndex) => {
    if (!isPositionedCodeRow(row)) return
    const name = normalizeSubgroupName(row.group)
    if (!name) return
    const start = Number(row.valuePosition)
    const end = getCodeRowPositionEnd(row) ?? start
    const key = name.toLowerCase()
    const current = ranges.get(key)
    if (!current) {
      ranges.set(key, { name, start, end, order: rowIndex })
      return
    }

    current.start = Math.min(current.start, start)
    current.end = Math.max(current.end, end)
  })

  return Array.from(ranges.values())
    .sort((left, right) => left.start - right.start || left.order - right.order)
    .map((range, index) => {
      const inferredDigits = Math.max(
        1,
        Math.floor(Number(totalDigits)) || 0,
        ...rows.map(row => String(row.optionCode || '').length)
      )
      const positionMode = range.start === 0 && range.end === inferredDigits - 1 ? 'any' as const : 'fixed' as const
      return {
        localKey: makeSubgroupLocalKey(range.name, range.start, range.end, index),
        name: range.name,
        length: Math.max(1, range.end - range.start + 1),
        positionMode,
        start: range.start,
        end: range.end,
      }
    })
}

export function resolveCodeItemSubgroups(group: CodeItemGroup): CodeItemSubgroup[] {
  const digits = getCodeItemDigits(group)
  const derived = deriveCompositeSubgroups(group.rows, digits)
  if (!group.subgroups?.length) return derived

  const derivedByName = new Map(derived.map(subgroup => [subgroup.name.toLowerCase(), subgroup]))
  const configuredNames = new Set(group.subgroups.map(subgroup => subgroup.name.toLowerCase()))
  const configured = group.subgroups.map((subgroup) => {
    const rowRange = derivedByName.get(subgroup.name.toLowerCase())
    if (!rowRange) {
      return subgroup.positionMode === 'any'
        ? { ...subgroup, length: digits, start: 0, end: digits - 1 }
        : subgroup
    }
    if (subgroup.positionMode === 'any' || rowRange.positionMode === 'any') {
      return { ...subgroup, positionMode: 'any' as const, length: digits, start: 0, end: digits - 1 }
    }
    if (!Number.isInteger(subgroup.start) || !Number.isInteger(subgroup.end)) return { ...subgroup, positionMode: 'fixed' as const }
    const start = Math.min(Number(subgroup.start), Number(rowRange.start))
    const end = Math.max(Number(subgroup.end), Number(rowRange.end))
    return { ...subgroup, positionMode: 'fixed' as const, start, end, length: end - start + 1 }
  })

  return [
    ...configured,
    ...derived.filter(subgroup => !configuredNames.has(subgroup.name.toLowerCase())),
  ]
}

export function isCompositeCodeItem(group: CodeItemGroup): boolean {
  if (group.authoringMode) return group.authoringMode === 'composite'
  return group.rows.some(isPositionedCodeRow)
}

export function deriveCompositeOptionRanges(group: CodeItemGroup): CompositeOptionRange[] {
  if (!isCompositeCodeItem(group)) return []

  const positionedSubgroups = positionSubgroups(resolveCodeItemSubgroups(group), getCodeItemDigits(group))
  const subgroupOrder = new Map(positionedSubgroups.map((subgroup, index) => [subgroup.name.toLowerCase(), index]))
  const ranges = new Map<string, CompositeOptionRange>()

  group.rows.forEach((row) => {
    if (!isPositionedCodeRow(row)) return
    const name = normalizeSubgroupName(row.group)
    const subgroup = positionedSubgroups.find(item => item.name.toLowerCase() === name.toLowerCase())
    if (!subgroup) return

    const start = Number(row.valuePosition)
    const end = getCodeRowPositionEnd(row) ?? start
    if (start < subgroup.start || end > subgroup.end || start > end) return

    const fragment = extractCompositeOptionFragment(row.optionCode, start, end)
    if (!fragment || fragment.length !== end - start + 1 || fragment.includes('*')) return

    const key = `${name.toLowerCase()}|||${start}-${end}`
    const current = ranges.get(key)
    if (current) {
      if (!current.options.includes(fragment)) current.options.push(fragment)
      return
    }
    ranges.set(key, { name, start, end, options: [fragment] })
  })

  return Array.from(ranges.values()).sort((left, right) => (
    (subgroupOrder.get(left.name.toLowerCase()) ?? Number.MAX_SAFE_INTEGER)
    - (subgroupOrder.get(right.name.toLowerCase()) ?? Number.MAX_SAFE_INTEGER)
    || left.start - right.start
    || left.end - right.end
  ))
}

export function buildCompositeOptionPattern(
  fragment: string,
  digits: number,
  start: number,
  end: number
): string {
  const normalizedDigits = Math.max(1, Math.floor(digits) || 1)
  const normalizedStart = Math.max(0, Math.floor(start) || 0)
  const normalizedEnd = Math.min(normalizedDigits - 1, Math.max(normalizedStart, Math.floor(end) || 0))
  const expectedLength = normalizedEnd - normalizedStart + 1
  const normalizedFragment = String(fragment || '').trim().toUpperCase()
  if (normalizedFragment.length !== expectedLength) return ''

  const characters = Array.from({ length: normalizedDigits }, () => '*')
  for (let offset = 0; offset < normalizedFragment.length; offset++) {
    characters[normalizedStart + offset] = normalizedFragment[offset]
  }
  return characters.join('')
}

export function extractCompositeOptionFragment(
  pattern: string,
  start: number,
  end: number
): string {
  const normalizedPattern = String(pattern || '').trim().toUpperCase()
  if (!normalizedPattern) return ''
  return normalizedPattern.slice(start, end + 1)
}

export function buildPositionableValues(
  pattern: string,
  start: number,
  end: number
): Record<string, string> {
  const values: Record<string, string> = {}
  const normalizedPattern = String(pattern || '').trim().toUpperCase()
  for (let position = start; position <= end; position++) {
    const character = normalizedPattern[position]
    if (character && character !== '*') values[String(position)] = character
  }
  return values
}

export function buildPatternFromPositionableValues(values: Record<string, string>, digits: number): string {
  const characters = Array.from({ length: Math.max(1, digits) }, () => '*')
  Object.entries(values || {}).forEach(([rawPosition, rawValue]) => {
    const position = Number(rawPosition)
    if (!Number.isInteger(position) || position < 0 || position >= characters.length) return
    const value = String(rawValue || '').toUpperCase()
    if (value.length === 1) characters[position] = value
  })
  return characters.join('')
}

export function normalizeCompositeSubgroups(
  subgroups: CodeItemSubgroup[],
  digits: number
): PositionedSubgroup[] {
  const normalizedDigits = Math.max(1, Math.floor(Number(digits)) || 1)
  return positionSubgroups(subgroups.map(subgroup => ({
    ...subgroup,
    name: normalizeSubgroupName(subgroup.name),
    positionMode: subgroup.positionMode === 'any' ? 'any' : 'fixed',
    length: subgroup.positionMode === 'any'
      ? normalizedDigits
      : Math.max(1, Math.floor(Number(subgroup.length)) || 1),
  })), normalizedDigits)
}

export function getCompositeGroupStructureErrors(subgroups: CodeItemSubgroup[], digits: number): string[] {
  const normalizedDigits = Math.max(1, Math.floor(Number(digits)) || 1)
  const errors: string[] = []
  const names = new Set<string>()
  const fixedGroups = subgroups.filter(subgroup => subgroup.positionMode !== 'any')
  const fixedLength = subgroups.reduce((total, subgroup) => (
    subgroup.positionMode === 'any' ? total : total + Math.max(1, Math.floor(Number(subgroup.length)) || 1)
  ), 0)

  if (subgroups.length === 0) errors.push('Add at least one group.')
  subgroups.forEach((subgroup) => {
    const name = normalizeSubgroupName(subgroup.name)
    const key = name.toLowerCase()
    if (!name) errors.push('Every group requires a name.')
    if (key && names.has(key)) errors.push(`Duplicate group name: ${name}.`)
    if (key) names.add(key)
  })
  if (fixedGroups.length > 0 && fixedLength !== normalizedDigits) {
    errors.push(`Fixed group lengths must total ${normalizedDigits} position${normalizedDigits === 1 ? '' : 's'}.`)
  }
  return Array.from(new Set(errors))
}

export function transformCompositeGroupStructure(
  group: CodeItemGroup,
  requestedSubgroups: CodeItemSubgroup[]
): { result: CompositeStructureResult | null; errors: string[] } {
  const digits = getCodeItemDigits(group)
  const structureErrors = getCompositeGroupStructureErrors(requestedSubgroups, digits)
  if (structureErrors.length > 0) return { result: null, errors: structureErrors }

  const previous = normalizeCompositeSubgroups(resolveCodeItemSubgroups(group), digits)
  const next = normalizeCompositeSubgroups(requestedSubgroups, digits)
  const nextByKey = new Map(next.map(subgroup => [subgroup.localKey, subgroup]))
  const previousByName = new Map(previous.map(subgroup => [subgroup.name.toLowerCase(), subgroup]))
  const errors: string[] = []
  const patternMap = new Map<string, string>()
  let movedOptionCount = 0

  previous.forEach((subgroup) => {
    if (nextByKey.has(subgroup.localKey)) return
    if (group.rows.some(row => normalizeSubgroupName(row.group).toLowerCase() === subgroup.name.toLowerCase())) {
      errors.push(`Group ${subgroup.name} cannot be removed while it contains options.`)
    }
  })

  const rows = group.rows.map((row) => {
    const previousSubgroup = previousByName.get(normalizeSubgroupName(row.group).toLowerCase())
    if (!previousSubgroup) return row
    const nextSubgroup = nextByKey.get(previousSubgroup.localKey)
    if (!nextSubgroup) return row

    const rowStart = Number(row.valuePosition)
    const rowEnd = getCodeRowPositionEnd(row)
    if (!Number.isInteger(rowStart) || rowEnd === null) return { ...row, group: nextSubgroup.name }

    if (previousSubgroup.positionMode === 'any' && nextSubgroup.positionMode === 'fixed') {
      if (rowStart < nextSubgroup.start || rowEnd > nextSubgroup.end) {
        errors.push(`Option ${row.optionCode} does not fit in ${nextSubgroup.name}'s new fixed range.`)
      }
      return { ...row, group: nextSubgroup.name }
    }

    if (previousSubgroup.positionMode === 'any' || nextSubgroup.positionMode === 'any') {
      return { ...row, group: nextSubgroup.name }
    }

    const relativeStart = rowStart - previousSubgroup.start
    const relativeEnd = rowEnd - previousSubgroup.start
    if (relativeStart < 0 || relativeEnd >= nextSubgroup.length) {
      errors.push(`Option ${row.optionCode} does not fit in ${nextSubgroup.name}'s new length.`)
      return { ...row, group: nextSubgroup.name }
    }

    const nextStart = nextSubgroup.start + relativeStart
    const nextEnd = nextSubgroup.start + relativeEnd
    const fragment = extractCompositeOptionFragment(row.optionCode, rowStart, rowEnd)
    const nextPattern = buildCompositeOptionPattern(fragment, digits, nextStart, nextEnd)
    if (!nextPattern) {
      errors.push(`Option ${row.optionCode} could not be moved safely.`)
      return { ...row, group: nextSubgroup.name }
    }
    if (nextPattern !== row.optionCode) {
      patternMap.set(row.optionCode, nextPattern)
      movedOptionCount += 1
    }
    return {
      ...row,
      group: nextSubgroup.name,
      optionCode: nextPattern,
      valuePosition: nextStart,
      valuePositionEnd: nextEnd,
    }
  })

  const seenPatterns = new Set<string>()
  rows.forEach((row) => {
    const pattern = String(row.optionCode || '').trim().toUpperCase()
    if (!pattern) return
    if (seenPatterns.has(pattern)) errors.push(`Duplicate option pattern after applying changes: ${pattern}.`)
    seenPatterns.add(pattern)
  })
  if (errors.length > 0) return { result: null, errors: Array.from(new Set(errors)) }

  const order = new Map(next.map((subgroup, index) => [subgroup.localKey, index]))
  const nextKeyByName = new Map(next.map(subgroup => [subgroup.name.toLowerCase(), subgroup.localKey]))
  const sortedRows = [...rows].sort((left, right) => (
    (order.get(nextKeyByName.get(normalizeSubgroupName(left.group).toLowerCase()) || '') ?? Number.MAX_SAFE_INTEGER)
    - (order.get(nextKeyByName.get(normalizeSubgroupName(right.group).toLowerCase()) || '') ?? Number.MAX_SAFE_INTEGER)
    || left.position - right.position
  )).map((row, position) => ({ ...row, position }))

  return {
    result: {
      group: {
        ...group,
        authoringMode: 'composite',
        subgroups: next,
        rows: sortedRows,
      },
      patternMap,
      movedOptionCount,
    },
    errors: [],
  }
}

/**
 * Resizes a Code Item without discarding values that no longer fit. Composite
 * rows that can be represented unambiguously are rebuilt; incompatible rows
 * are kept verbatim so the author can repair them before Save/Submit.
 */
export function transformCodeItemResize(
  group: CodeItemGroup,
  requestedDigits: number,
  requestedSubgroups: CodeItemSubgroup[] = resolveCodeItemSubgroups(group)
): { result: CodeItemResizeResult | null; errors: string[] } {
  const nextDigits = Math.max(1, Math.floor(Number(requestedDigits)) || 1)
  const currentDigits = getCodeItemDigits(group)
  const [absoluteStart] = group.codeItemPos.posRange
  const resizedBase: CodeItemGroup = {
    ...group,
    codeItemPos: {
      ...group.codeItemPos,
      posRange: [absoluteStart, absoluteStart + nextDigits - 1],
      label: `${group.codeItemPos.code} [${absoluteStart}-${absoluteStart + nextDigits - 1}]`,
    },
  }

  if (!isCompositeCodeItem(group)) {
    const affectedOptions = group.rows.flatMap((row) => {
      const value = String(row.optionCode || '').trim().toUpperCase()
      if (value === '*' || value === '#' || value.length === nextDigits) return []
      return [{
        rowId: row.rowId,
        optionCode: row.optionCode,
        reason: `Option ${value || '(empty)'} must be updated to ${nextDigits} positions.`,
      }]
    })
    return {
      result: {
        group: resizedBase,
        patternMap: new Map(),
        impact: { affectedOptions, safelyRemappedOptions: 0 },
      },
      errors: [],
    }
  }

  const structureErrors = getCompositeGroupStructureErrors(requestedSubgroups, nextDigits)
  if (structureErrors.length > 0) return { result: null, errors: structureErrors }

  const previous = normalizeCompositeSubgroups(resolveCodeItemSubgroups(group), currentDigits)
  const next = normalizeCompositeSubgroups(requestedSubgroups, nextDigits)
  const previousByName = new Map(previous.map(subgroup => [subgroup.name.toLowerCase(), subgroup]))
  const nextByKey = new Map(next.map(subgroup => [subgroup.localKey, subgroup]))
  const removedWithOptions = previous.filter(subgroup => (
    !nextByKey.has(subgroup.localKey)
    && group.rows.some(row => normalizeSubgroupName(row.group).toLowerCase() === subgroup.name.toLowerCase())
  ))
  if (removedWithOptions.length > 0) {
    return {
      result: null,
      errors: removedWithOptions.map(subgroup => `Group ${subgroup.name} cannot be removed while it contains options.`),
    }
  }

  const patternMap = new Map<string, string>()
  const affectedOptions: ResizeIssue[] = []
  let safelyRemappedOptions = 0
  const rows = group.rows.map((row) => {
    const previousSubgroup = previousByName.get(normalizeSubgroupName(row.group).toLowerCase())
    const nextSubgroup = previousSubgroup ? nextByKey.get(previousSubgroup.localKey) : undefined
    if (!previousSubgroup || !nextSubgroup) return row

    const rowStart = Number(row.valuePosition)
    const rowEnd = getCodeRowPositionEnd(row)
    if (!Number.isInteger(rowStart) || rowEnd === null || rowStart > rowEnd) {
      affectedOptions.push({ rowId: row.rowId, optionCode: row.optionCode, reason: `Option ${row.optionCode || '(empty)'} has no valid position range.` })
      return { ...row, group: nextSubgroup.name }
    }

    let nextStart = rowStart
    let nextEnd = rowEnd
    if (previousSubgroup.positionMode !== 'any' && nextSubgroup.positionMode !== 'any') {
      const relativeStart = rowStart - previousSubgroup.start
      const relativeEnd = rowEnd - previousSubgroup.start
      nextStart = nextSubgroup.start + relativeStart
      nextEnd = nextSubgroup.start + relativeEnd
    }

    const fitsSubgroup = nextStart >= nextSubgroup.start && nextEnd <= nextSubgroup.end
    const fitsCodeItem = nextStart >= 0 && nextEnd < nextDigits
    const fragment = extractCompositeOptionFragment(row.optionCode, rowStart, rowEnd)
    const fragmentIsValid = fragment.length === rowEnd - rowStart + 1 && !fragment.includes('*')
    if (!fitsSubgroup || !fitsCodeItem || !fragmentIsValid) {
      affectedOptions.push({
        rowId: row.rowId,
        optionCode: row.optionCode,
        reason: `Option ${row.optionCode || '(empty)'} does not fit in ${nextSubgroup.name}'s resized range.`,
      })
      return { ...row, group: nextSubgroup.name }
    }

    const nextPattern = buildCompositeOptionPattern(fragment, nextDigits, nextStart, nextEnd)
    if (!nextPattern) {
      affectedOptions.push({ rowId: row.rowId, optionCode: row.optionCode, reason: `Option ${row.optionCode || '(empty)'} could not be resized safely.` })
      return { ...row, group: nextSubgroup.name }
    }
    if (nextPattern !== row.optionCode || nextStart !== rowStart || nextEnd !== rowEnd) {
      patternMap.set(row.optionCode, nextPattern)
      safelyRemappedOptions += 1
    }
    return {
      ...row,
      group: nextSubgroup.name,
      optionCode: nextPattern,
      valuePosition: nextStart,
      valuePositionEnd: nextEnd,
    }
  })

  const seenPatterns = new Set<string>()
  const duplicateErrors: string[] = []
  rows.forEach((row) => {
    const pattern = String(row.optionCode || '').trim().toUpperCase()
    if (!pattern) return
    if (seenPatterns.has(pattern)) duplicateErrors.push(`Duplicate option pattern after resizing: ${pattern}.`)
    seenPatterns.add(pattern)
  })
  if (duplicateErrors.length > 0) return { result: null, errors: Array.from(new Set(duplicateErrors)) }

  return {
    result: {
      group: {
        ...resizedBase,
        authoringMode: 'composite',
        subgroups: next,
        rows,
      },
      patternMap,
      impact: { affectedOptions, safelyRemappedOptions },
    },
    errors: [],
  }
}

function rowMatchesSelectedRange(row: CodeRow, digits: number, start: number, end: number): boolean {
  const pattern = String(row.optionCode || '').trim().toUpperCase()
  if (pattern.length !== digits || start > end) return false
  return Array.from(pattern).every((character, position) => (
    position >= start && position <= end ? character !== '*' : character === '*'
  ))
}

export function validateCompositeCodeItem(group: CodeItemGroup): string[] {
  if (!isCompositeCodeItem(group)) return []

  const errors: string[] = []
  const digits = getCodeItemDigits(group)
  const subgroups = resolveCodeItemSubgroups(group)
  const positionedSubgroups = positionSubgroups(subgroups, digits)
  const coveredPositions = new Set(positionedSubgroups.flatMap(subgroup => (
    Array.from({ length: subgroup.end - subgroup.start + 1 }, (_, offset) => subgroup.start + offset)
  )))
  const subgroupNames = new Set<string>()

  if (Array.from({ length: digits }, (_, position) => position).some(position => !coveredPositions.has(position))) {
    errors.push(`Subgroup lengths must total ${digits} position${digits === 1 ? '' : 's'}.`)
  }

  positionedSubgroups.forEach((subgroup) => {
    const normalizedName = normalizeSubgroupName(subgroup.name)
    const nameKey = normalizedName.toLowerCase()
    if (!normalizedName) errors.push('Every subgroup requires a name.')
    if (subgroupNames.has(nameKey)) errors.push(`Duplicate subgroup name: ${normalizedName}.`)
    subgroupNames.add(nameKey)

    const subgroupRows = group.rows.filter(row => normalizeSubgroupName(row.group).toLowerCase() === nameKey)
    if (subgroupRows.length === 0) {
      errors.push(`Subgroup ${normalizedName || '(unnamed)'} requires at least one option.`)
      return
    }

    for (let position = subgroup.start; position <= subgroup.end; position++) {
      const covered = subgroupRows.some(row => {
        if (!isPositionedCodeRow(row)) return false
        const rowStart = Number(row.valuePosition)
        const rowEnd = getCodeRowPositionEnd(row) ?? rowStart
        return rowStart >= subgroup.start
          && rowEnd <= subgroup.end
          && position >= rowStart
          && position <= rowEnd
          && rowMatchesSelectedRange(row, digits, rowStart, rowEnd)
      })
      if (!covered) errors.push(`Subgroup ${normalizedName || '(unnamed)'} requires at least one option for position ${position + 1}.`)
    }
  })

  const seenPatterns = new Set<string>()
  group.rows.forEach((row) => {
    const name = normalizeSubgroupName(row.group)
    const subgroup = positionedSubgroups.find(item => item.name.toLowerCase() === name.toLowerCase())
    if (!subgroup || !isPositionedCodeRow(row)) {
      errors.push('Composite Code Items cannot mix positioned and unpositioned options.')
      return
    }

    const rowEnd = getCodeRowPositionEnd(row)
    const rowStart = Number(row.valuePosition)
    if (rowEnd === null || rowStart > rowEnd || rowStart < subgroup.start || rowEnd > subgroup.end) {
      errors.push(`Option ${row.optionCode || '(empty)'} is outside subgroup ${subgroup.name}'s position range.`)
    }

    const pattern = String(row.optionCode || '').trim().toUpperCase()
    if (pattern.length !== digits) {
      errors.push(`Option ${pattern || '(empty)'} must contain ${digits} positions.`)
      return
    }

    for (let position = 0; position < pattern.length; position++) {
      const insideRange = rowEnd !== null && position >= rowStart && position <= rowEnd
      if (insideRange ? pattern[position] === '*' : pattern[position] !== '*') {
        errors.push(`Option ${pattern} has an invalid Any/value position for its selected range.`)
        break
      }
    }

    if (seenPatterns.has(pattern)) errors.push(`Duplicate option pattern: ${pattern}.`)
    seenPatterns.add(pattern)
  })

  return Array.from(new Set(errors))
}

function conditionGroupReferencesCode(group: ConditionGroup | null | undefined, referencedCode: string): boolean {
  if (!group) return false
  return Boolean(
    group.conditions?.some(condition => normalizeCodeItemReference(condition.reference) === normalizeCodeItemReference(referencedCode))
    || group.groups?.some(subgroup => conditionGroupReferencesCode(subgroup, referencedCode))
  )
}

function ifNodeReferencesCode(node: IfNode, referencedCode: string): boolean {
  return normalizeCodeItemReference(node.reference) === normalizeCodeItemReference(referencedCode)
    || conditionGroupReferencesCode(node.conditionGroup, referencedCode)
    || Boolean(node.consequenceGroups?.some(group => conditionGroupReferencesCode(group, referencedCode)))
    || Boolean(node.if && ifNodeReferencesCode(node.if, referencedCode))
    || Boolean(node.else && ifNodeReferencesCode(node.else, referencedCode))
}

function replacePatternsInDescription(
  description: string | null | undefined,
  patternMap: Map<string, string>
): string | null | undefined {
  if (!description) return description
  let next = description
  patternMap.forEach((replacement, pattern) => {
    next = next.split(pattern).join(replacement)
  })
  return next
}

function remapLegacyPattern(value: string, positionMap: Map<number, number>): string {
  if (!value.includes('*')) return value
  const characters = Array.from(value)
  const remapped = Array.from({ length: characters.length }, () => '*')
  characters.forEach((character, position) => {
    const nextPosition = positionMap.get(position) ?? position
    if (nextPosition >= 0 && nextPosition < remapped.length) remapped[nextPosition] = character
  })
  return remapped.join('')
}

function remapReferencedValue(
  value: string | null | undefined,
  positionMap: Map<number, number>,
  patternMap: Map<string, string>,
  selective: boolean
): string | null | undefined {
  if (!value) return value
  return patternMap.get(value) || (selective ? value : remapLegacyPattern(value, positionMap))
}

type RemapCompositeRuleOptions = {
  selective?: boolean
  digits?: number
  sourceDigits?: number
  descriptionMap?: Map<string, string>
}

function remapRuleDescription(
  description: string | null | undefined,
  patternMap: Map<string, string>,
  options: RemapCompositeRuleOptions
): string | null | undefined {
  return replacePatternsInDescription(
    replacePatternsInDescription(description, patternMap),
    options.descriptionMap || new Map<string, string>()
  )
}

function remapConditionGroup(
  group: ConditionGroup | null | undefined,
  referencedCode: string,
  positionMap: Map<number, number>,
  patternMap: Map<string, string>,
  options: RemapCompositeRuleOptions
): ConditionGroup | null | undefined {
  if (!group) return group
  return {
    ...group,
    conditions: group.conditions?.map((condition) => {
      if (normalizeCodeItemReference(condition.reference) !== normalizeCodeItemReference(referencedCode)) return condition
      let remappedValues = condition.values
      if (condition.values) {
        if (options.selective && options.digits) {
          const pattern = buildPatternFromPositionableValues(condition.values, options.sourceDigits || options.digits)
          const replacement = patternMap.get(pattern)
          remappedValues = replacement
            ? Object.fromEntries(Array.from(replacement).flatMap((value, position) => value === '*' ? [] : [[String(position), value]]))
            : condition.values
        } else {
          remappedValues = Object.fromEntries(
            Object.entries(condition.values)
              .map(([key, value]) => [String(positionMap.get(Number(key)) ?? Number(key)), value])
              .sort(([left], [right]) => Number(left) - Number(right))
          )
        }
      }
      return {
        ...condition,
        value: remapReferencedValue(condition.value, positionMap, patternMap, Boolean(options.selective)),
        from: remapReferencedValue(condition.from, positionMap, patternMap, Boolean(options.selective)),
        to: remapReferencedValue(condition.to, positionMap, patternMap, Boolean(options.selective)),
        values: remappedValues,
      }
    }) ?? group.conditions,
    groups: group.groups?.map(subgroup => (
      remapConditionGroup(subgroup, referencedCode, positionMap, patternMap, options) as ConditionGroup
    )) ?? group.groups,
  }
}

function remapIfNode(
  node: IfNode,
  referencedCode: string,
  positionMap: Map<number, number>,
  patternMap: Map<string, string>,
  options: RemapCompositeRuleOptions
): IfNode {
  const matchesReference = normalizeCodeItemReference(node.reference) === normalizeCodeItemReference(referencedCode)
  return {
    ...node,
    value: matchesReference ? remapReferencedValue(node.value, positionMap, patternMap, Boolean(options.selective)) : node.value,
    description: remapRuleDescription(node.description, patternMap, options),
    conditionGroup: remapConditionGroup(node.conditionGroup, referencedCode, positionMap, patternMap, options),
    consequenceGroups: node.consequenceGroups?.map(group => (
      remapConditionGroup(group, referencedCode, positionMap, patternMap, options) as ConditionGroup
    )) ?? node.consequenceGroups,
    if: node.if ? remapIfNode(node.if, referencedCode, positionMap, patternMap, options) : node.if,
    else: node.else ? remapIfNode(node.else, referencedCode, positionMap, patternMap, options) : node.else,
  }
}

export function remapCompositeRulePositions(
  rule: RuleNode | null | undefined,
  referencedCode: string,
  positionMap: Map<number, number>,
  patternMap: Map<string, string>,
  options: RemapCompositeRuleOptions = {}
): RuleNode | null | undefined {
  if (!rule || rule.type === 'text') return rule
  const referencesCode = rule.type === 'while'
    ? normalizeCodeItemReference(rule.reference) === normalizeCodeItemReference(referencedCode)
    : ifNodeReferencesCode(rule, referencedCode)
  const effectiveOptions = referencesCode ? options : { ...options, descriptionMap: undefined }
  if (rule.type === 'while') {
    return {
      ...rule,
      value: referencesCode ? String(remapReferencedValue(rule.value, positionMap, patternMap, Boolean(options.selective)) || '') : rule.value,
      description: remapRuleDescription(rule.description, patternMap, effectiveOptions),
    }
  }
  return remapIfNode(rule, referencedCode, positionMap, patternMap, effectiveOptions)
}

export type RuleResizeIssue = {
  sourceGroupIndex: number
  sourceRowIndex: number
  sourceCodeItemCode: string
  sourceOptionCode: string
  reason: string
}

export type RuleResizeImpact = {
  affectedRules: RuleResizeIssue[]
  safelyRemappedRules: number
}

function inspectConditionGroupResize(
  group: ConditionGroup | null | undefined,
  referencedCode: string,
  sourceDigits: number,
  nextDigits: number,
  patternMap: Map<string, string>
): { affected: boolean; remapped: boolean } {
  if (!group) return { affected: false, remapped: false }
  let affected = false
  let remapped = false
  group.conditions?.forEach((condition) => {
    if (normalizeCodeItemReference(condition.reference) !== normalizeCodeItemReference(referencedCode)) return
    if (condition.values) {
      const sourcePattern = buildPatternFromPositionableValues(condition.values, sourceDigits)
      if (patternMap.has(sourcePattern)) remapped = true
      else if (Object.keys(condition.values).some(key => {
        const position = Number(key)
        return !Number.isInteger(position) || position < 0 || position >= nextDigits
      })) affected = true
    }
    ;[condition.value, condition.from, condition.to].forEach((value) => {
      if (!value) return
      if (patternMap.has(value)) remapped = true
      else if (value.length > 1 && value.includes('*') && value.length !== nextDigits) affected = true
    })
  })
  group.groups?.forEach((child) => {
    const result = inspectConditionGroupResize(child, referencedCode, sourceDigits, nextDigits, patternMap)
    affected = affected || result.affected
    remapped = remapped || result.remapped
  })
  return { affected, remapped }
}

function inspectRuleResize(
  rule: RuleNode | null | undefined,
  referencedCode: string,
  sourceDigits: number,
  nextDigits: number,
  patternMap: Map<string, string>
): { affected: boolean; remapped: boolean } {
  if (!rule || rule.type === 'text') return { affected: false, remapped: false }
  if (rule.type === 'while') {
    if (normalizeCodeItemReference(rule.reference) !== normalizeCodeItemReference(referencedCode)) {
      return { affected: false, remapped: false }
    }
    return {
      affected: rule.value.length > 1 && rule.value.includes('*') && rule.value.length !== nextDigits && !patternMap.has(rule.value),
      remapped: patternMap.has(rule.value),
    }
  }

  let affected = false
  let remapped = false
  if (normalizeCodeItemReference(rule.reference) === normalizeCodeItemReference(referencedCode) && rule.value) {
    remapped = patternMap.has(rule.value)
    affected = rule.value.length > 1 && rule.value.includes('*') && rule.value.length !== nextDigits && !remapped
  }
  ;[rule.conditionGroup, ...(rule.consequenceGroups || [])].forEach((conditionGroup) => {
    const result = inspectConditionGroupResize(conditionGroup, referencedCode, sourceDigits, nextDigits, patternMap)
    affected = affected || result.affected
    remapped = remapped || result.remapped
  })
  ;[rule.if, rule.else].forEach((child) => {
    const result = inspectRuleResize(child, referencedCode, sourceDigits, nextDigits, patternMap)
    affected = affected || result.affected
    remapped = remapped || result.remapped
  })
  return { affected, remapped }
}

export function getRuleResizeImpact(
  groups: CodeItemGroup[],
  referencedCode: string,
  nextDigits: number,
  patternMap: Map<string, string>,
  sourceDigits: number = nextDigits
): RuleResizeImpact {
  const affectedRules: RuleResizeIssue[] = []
  let safelyRemappedRules = 0
  groups.forEach((group, sourceGroupIndex) => {
    group.rows.forEach((row, sourceRowIndex) => {
      const result = inspectRuleResize(row.rule, referencedCode, sourceDigits, nextDigits, patternMap)
      if (result.remapped) safelyRemappedRules += 1
      if (result.affected) {
        affectedRules.push({
          sourceGroupIndex,
          sourceRowIndex,
          sourceCodeItemCode: group.codeItemPos.code,
          sourceOptionCode: row.optionCode,
          reason: `Rule on Code ${group.codeItemPos.code} value ${row.optionCode || '(blank)'} uses a position removed from Code ${referencedCode}.`,
        })
      }
    })
  })
  return { affectedRules, safelyRemappedRules }
}

export function findInvalidPositionableRuleReferences(groups: CodeItemGroup[]): RuleResizeIssue[] {
  const issues = groups.flatMap((referencedGroup) => (
    getRuleResizeImpact(
      groups,
      referencedGroup.codeItemPos.code,
      getCodeItemDigits(referencedGroup),
      new Map<string, string>()
    ).affectedRules
  ))
  const seen = new Set<string>()
  return issues.filter((issue) => {
    const key = `${issue.sourceGroupIndex}:${issue.sourceRowIndex}:${issue.reason}`
    if (seen.has(key)) return false
    seen.add(key)
    return true
  })
}
